// What the assistant has cost: calls and tokens today, this week and this
// month, by mode, and the last refusal in full. The numbers are the API's own,
// so a bill can be checked against them; a refusal here is usually the whole
// answer to "why has it gone quiet".
import { useEffect, useState } from 'react'
import { api } from '@/runtime'
import type { UsageCounts, UsageReport } from '@/db/types'
import { dateOf, fmtDateShort } from '@/lib/time'
import { cx } from './ui'
import { Phrases } from './Phrases'

const fmt = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 10_000
      ? `${Math.round(n / 1000)}k`
      : n >= 1000
        ? `${(n / 1000).toFixed(1)}k`
        : String(n)

const MODE_LABEL: Record<string, string> = {
  voice: 'Spoken turns',
  chat: 'Quick chat',
  summary: 'Memory summaries',
  plan: 'Daily plan',
  triage: 'Inbox triage',
  capture: 'Capture filing',
  ask: 'Ask',
  refine: 'Refine',
  extract: 'Page extract',
  write: 'Page write',
}

/** Why the model refused, in words; the API's own message stays under it. */
export const refusalWords = (text: string): string => {
  const t = text.toLowerCase()
  if (t.includes('overloaded')) return 'Anthropic was overloaded and asked to try again in a moment.'
  if (t.includes('rate limit') || t.includes('rate_limit') || t.includes('429'))
    return 'Too many requests at once; Anthropic asked to slow down.'
  if (t.includes('budget')) return "Today's token budget was used up."
  if (t.includes('credit') || t.includes('billing')) return 'The Anthropic account is out of credit.'
  if (t.includes('timeout') || t.includes('deadline')) return 'The model took too long to answer.'
  return 'The model refused the request.'
}

export const UsagePanel = ({
  big,
  period,
}: {
  big?: boolean
  /** set by a page that has its own period picker (Agent stats): this one then shows none */
  period?: 'today' | 'week' | 'month'
}) => {
  const [report, setReport] = useState<UsageReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [picked, setWin] = useState<'today' | 'week' | 'month'>('today')
  const win = period ?? picked
  useEffect(() => {
    let stale = false
    api
      .assistantUsage()
      .then((r) => {
        if (!stale) setReport(r)
      })
      .catch((e: Error) => {
        if (!stale) setError(e.message)
      })
    return () => {
      stale = true
    }
  }, [])
  if (error) return <p className="text-sm text-amber-300/90">{error}</p>
  if (!report) return <p className={cx('text-faint', big ? 'text-[15px]' : 'text-xs')}>Loading…</p>
  const w = report[win]
  const modes = Object.entries(w.modes).sort(
    (a, b) => b[1].input + b[1].cached + b[1].output - (a[1].input + a[1].cached + a[1].output),
  )
  // a column of nothing says nothing: Errors shows only when there were some
  const errors = w.total.errors > 0
  const cell = (u: UsageCounts) => (
    <>
      <td className="text-right tabular-nums px-2">{u.calls}</td>
      {errors && (
        <td className={cx('text-right tabular-nums px-2', u.errors > 0 && 'text-amber-300')}>
          {u.errors || ''}
        </td>
      )}
      <td className="text-right tabular-nums px-2">{fmt(u.input)}</td>
      <td className="text-right tabular-nums px-2 text-faint">{fmt(u.cached)}</td>
      <td className="text-right tabular-nums px-2">{fmt(u.output)}</td>
    </>
  )
  const max = Math.max(1, ...report.days.map((d) => d.total.input + d.total.cached + d.total.output))
  const todayTotal = report.today.total.input + report.today.total.cached + report.today.total.output
  return (
    <div className={cx('flex flex-col gap-3', big ? 'text-[16px]' : 'text-sm')} data-testid="usage-panel">
      <div className="flex items-center gap-2">
        {!period &&
          (['today', 'week', 'month'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setWin(k)}
              className={cx(
                'rounded-full px-3 py-1 text-xs',
                win === k ? 'bg-accent text-on-accent' : 'bg-raised text-muted',
              )}
              aria-pressed={win === k}
            >
              {k === 'today' ? 'Today' : k === 'week' ? '7 days' : '30 days'}
            </button>
          ))}
        <span className="text-xs text-faint ml-auto">Tokens in, cached and out</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-faint">
            <tr>
              <th className="text-left font-normal px-2">Mode</th>
              <th className="text-right font-normal px-2">Calls</th>
              {errors && <th className="text-right font-normal px-2">Errors</th>}
              <th className="text-right font-normal px-2">In</th>
              <th className="text-right font-normal px-2">Cached</th>
              <th className="text-right font-normal px-2">Out</th>
            </tr>
          </thead>
          <tbody>
            {modes.map(([mode, u]) => (
              <tr key={mode} className="border-t border-line">
                <td className="px-2 py-1">{MODE_LABEL[mode] ?? mode}</td>
                {cell(u)}
              </tr>
            ))}
            <tr className="border-t border-line font-medium">
              <td className="px-2 py-1">Total</td>
              {cell(w.total)}
            </tr>
          </tbody>
        </table>
      </div>
      {report.budget > 0 && (
        <div data-testid="usage-budget">
          <div className="flex justify-between text-xs text-faint mb-1">
            <span>Today's budget</span>
            <span className="tabular-nums">
              {fmt(todayTotal)} of {fmt(report.budget)}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-raised overflow-hidden">
            <div
              className={cx(
                'h-full rounded-full',
                todayTotal >= report.budget ? 'bg-amber-300' : 'bg-accent',
              )}
              style={{ width: `${Math.min(100, Math.round((todayTotal / report.budget) * 100))}%` }}
            />
          </div>
        </div>
      )}
      {/* the month, a bar a day */}
      <div className="flex items-end gap-[2px] h-10" aria-label="Tokens a day over the last month">
        {report.days.map((d) => {
          const t = d.total.input + d.total.cached + d.total.output
          return (
            <span
              key={d.date}
              className={cx('flex-1 rounded-sm', d.total.errors ? 'bg-amber-300/70' : 'bg-accent/70')}
              style={{ height: `${Math.max(2, Math.round((t / max) * 40))}px` }}
              title={`${d.date}: ${fmt(t)} tokens, ${d.total.calls} calls`}
            />
          )
        })}
      </div>
      {report.last_error && (
        <div className="rounded-[var(--radius-card-inner)] border border-line bg-raised px-3 py-2 text-xs">
          <Phrases
            className="text-muted mb-1"
            parts={[
              'Last refusal',
              fmtDateShort(dateOf(new Date(report.last_error.at))),
              MODE_LABEL[report.last_error.mode] ?? report.last_error.mode,
            ]}
          />
          <div className="text-fg text-sm">{refusalWords(report.last_error.text)}</div>
          <div className="text-faint break-words mt-0.5">It said: {report.last_error.text}</div>
        </div>
      )}
    </div>
  )
}
